import { useEffect, useState } from 'react'
import { API, graphqlOperation } from 'aws-amplify'

const listOccupancyQuery = `
    query ListOccupancy($pk: ID!, $sk: String) {
        listOccupancy(pk: $pk, sk: $sk) {
            items {
                pk
                sk
                d1
                d2
            }
        }
    }
`

const operation = `query ($sk: String, $d2: ID) {
    getOccupancy(sk: $sk, d2: $d2) {
        ... on Occupant {
            pk
            sk
            d1
            d2
            firstName
            lastName
            roles
            primaryContact
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            lastLogin
            registrationDate
            extraFields {
                key
                value
            }
            email
            version
            __typename
        }
        ... on EvacuationAssistance {
            pk
            sk
            d1
            assistanceNeeded
            email
            createdAt
            endDate
            floor
            firstName
            lastName
            phone {
                number
                extension
            }
            startDate
            suite
            version
            buddies
            __typename
        }
        ... on Building {
            pk
            sk
            d1
            name
            settings {
                systemConfig {
                    suites
                }
            }
            __typename
        }
        ... on Property {
            pk
            sk
            d1
            name
            settings {
                multiBuilding
            }
            __typename
        }
        ... on Client {
            pk
            sk
            d1
            name
            __typename
        }
        ... on Tenant {
            pk
            sk
            d1
            name
            floors
            suites
            __typename
        }
        ... on TrainingResult {
            certificateType
            completedAt
            createdAt
            d1
            d2
            expiredAt
            firstName
            lastName
            pk
            quizzesCompleted
            sk
            title {
                language
                text
            }
            updatedAt
            userType
            version
            videosCompleted
            __typename
        }
    }
}`

export const clearOccupancyCache = (id) => {
    // No-op for compatibility
}

export const updateTrainingResultInCache = (d2, updatedTrainingResult) => {
    // No-op for compatibility
}

export const useGetOccupancy = (id) => {

    const [data, setData] = useState([])
    const [errors, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [refetchTrigger, setRefetchTrigger] = useState(0)

    const handler = _data => {
        setData(_data)
        setLoading(false)
    }

    const refetch = () => {
        setRefetchTrigger(prev => prev + 1)
    }

    useEffect(() => {
        async function fetch(id) {
            setLoading(true)
            try {
                // Step 1: Get occupancy data (this gives us the user's pk)
                const response = await API.graphql(graphqlOperation(operation, { d2: id }))
                const occupancyData = response.data.getOccupancy

                // Handle deleted occupancy
                if (!occupancyData || occupancyData.length === 0) {
                    setErrors([{ message: 'Occupancy not found' }])
                    setLoading(false)
                    return
                }

                // Find the user's pk
                const userPk = occupancyData.find(item => item.pk)?.pk

                if (!userPk) {
                    setErrors([{ message: 'Invalid occupancy data' }])
                    setLoading(false)
                    return
                }

                // Step 2: Get ALL occupancies for this user
                const occupanciesResponse = await API.graphql(graphqlOperation(listOccupancyQuery, {
                    pk: userPk,
                    sk: 'OCCUPANCY'
                }))

                const allOccupancies = occupanciesResponse.data.listOccupancy.items || []

                // Step 3: Query getOccupancy for EACH d2 to get all training results
                const trainingResultsMap = new Map()

                const occupancyQueries = allOccupancies.map(occ =>
                    API.graphql(graphqlOperation(operation, { d2: occ.d2 }))
                        .then(res => {
                            const data = res.data.getOccupancy || []
                            const trainingResults = data.filter(item => item.__typename === 'TrainingResult')
                            return trainingResults
                        })
                        .catch(err => {
                            console.error('Error fetching occupancy data for d2:', occ.d2, err)
                            return []
                        })
                )

                const allTrainingArrays = await Promise.all(occupancyQueries)
                const allTrainingResults = allTrainingArrays.flat()

                // Deduplicate by sk+d2
                allTrainingResults.forEach(tr => {
                    const key = `${tr.sk}_${tr.d2}`
                    if (!trainingResultsMap.has(key)) {
                        trainingResultsMap.set(key, tr)
                    }
                })

                const uniqueTrainingResults = Array.from(trainingResultsMap.values())

                // Step 4: Merge occupancy data with all training results
                const mergedData = [...occupancyData, ...uniqueTrainingResults]
                handler(mergedData)
            } catch (response) {
                console.error(response)
                setErrors(response.errors)
                setLoading(false)
            }
        }

        if (id) {
            fetch(id)
        }

    }, [id, refetchTrigger])

    return { data, errors, loading, refetch }
}